import { Box, Button, Center, Flex, Heading, SystemStyleObject, Text } from '@chakra-ui/react';
import {
  AppLink,
  ChevronDownIcon,
  LANDING_ROUTES,
  pxToRem,
  useKollections,
  VideoAutoplay,
} from '@cyberkongz/ui';
import { ReactNode, useMemo } from 'react';

import { LandingKollectionType } from './types';

const KOLLECTION_BANNER_CONTENT: {
  [key in LandingKollectionType]: {
    title: ReactNode;
    description: string;
    bg: string;
    videoStyles?: SystemStyleObject;
  };
} = {
  genesis: {
    title: 'get to know the Genesis',
    description: `Every CyberKong is unique and owns randomized items with different rarities. A few are super rare and even animated!`,
    bg: 'linear-gradient(91.74deg, #A30083 8.36%, #5F0045 91.06%)',
    videoStyles: {
      transform: 'translateX(-10%)',
    },
  },
  baby: {
    title: 'get to know the Babies',
    description: `Each Baby is bred by two Genesis CyberKongz and every Baby CyberKong is unique and owns randomized items with different rarities.`,
    bg: 'linear-gradient(91.74deg, #016641 8.36%, #033125 91.06%)',
    videoStyles: {
      maxHeight: '90%',
    },
  },
  vx: {
    title: (
      <>
        get to know the VX
        <Text as="span" fontSize="0.65em">
          s
        </Text>
      </>
    ),
    description: `CyberKongz VX are unique and randomly generated 3D NFT Social Avatars for the Metaverse and upcoming interoperability with The Sandbox.`,
    bg: 'linear-gradient(91.74deg, #017C8F 8.36%, #004452 91.06%)',
    videoStyles: {
      maxHeight: '100%',
      position: 'relative',
    },
  },
};

export function KollectionsBanner() {
  const { kollection } = useKollections();
  const { title, description, bg, videoStyles } =
    KOLLECTION_BANNER_CONTENT[kollection as LandingKollectionType];

  return useMemo(
    () => (
      <Flex
        css={{
          bg,
          height: [pxToRem(72), pxToRem(100), pxToRem(120)],
        }}
      >
        <Flex css={{ alignItems: 'center', width: '100%' }}>
          <Flex css={{ alignItems: 'center' }}>
            <Flex
              css={{
                width: [pxToRem(106), pxToRem(184), pxToRem(190)],
                height: [pxToRem(72), pxToRem(100), pxToRem(120)],
              }}
            >
              <VideoAutoplay
                src={`/static/kollections/banner_${kollection}.webm`}
                posterExtension="png"
                css={{
                  mx: 'auto',
                  maxHeight: '100%',
                  maxWidth: 'none',
                  alignSelf: 'flex-end',
                  ...videoStyles,
                }}
              />
            </Flex>
            <Box>
              <Heading
                variant="display"
                css={{ textShadow: 'none', fontSize: ['2xl', '3xl', '4xl'] }}
              >
                {title}
              </Heading>
              <Text
                css={{ display: ['none', undefined, 'block'], fontSize: 'sm', maxW: '35.42em' }}
              >
                {description}
              </Text>
            </Box>
          </Flex>
          <Flex
            css={{
              flex: 1,
              px: 6,
              justifyContent: 'center',
            }}
          >
            <AppLink variant="noHover" href={LANDING_ROUTES.KOLLECTION(kollection)}>
              <Button variant="display" size="sm" display={['none', 'block']}>
                Learn&nbsp; More
              </Button>
              <Center
                css={{
                  display: ['flex', 'none'],
                  bg: ' background',
                  w: 6,
                  h: 6,
                  borderRadius: '50%',
                }}
              >
                <ChevronDownIcon
                  color="text"
                  width={3}
                  transform={`translateX(${pxToRem(1)}) rotateZ(-90deg)`}
                />
              </Center>
            </AppLink>
          </Flex>
        </Flex>
      </Flex>
    ),
    [kollection]
  );
}
